import React from 'react'
import styles from './MicrositeContactSectionForm.module.css'
import TextInput from '../../../components/TextInput/TextInput'
import MicrositeRepeatableRows from '../../../components/MicrositeRepeatableRows/MicrositeRepeatableRows'

// ──────────────────────────────────────────────────────────────
// Default skeleton matching the real production content shape.
// Note: address / phone / email / map URL are NOT part of this
// section's content — those live on the store master record
// (storeAddress, storePhoneNumber, storeEmail, mapUrl in
// osm_store_micro_site_master) and are edited on the General
// settings screen, not here.
// ──────────────────────────────────────────────────────────────
const DEFAULT_CONTENT = {
    title: '',
    highlightedTitle: '',
    subtitle: '',
    description: '',
    workingHours: { title: 'Working Hours', schedule: [] }
}

const EMPTY_SCHEDULE_ROW = { day: '', time: '' }

// Backfills any missing key so older or partially-saved content
// still renders without crashing.
const withDefaults = (content) => ({
    title: content?.title ?? DEFAULT_CONTENT.title,
    highlightedTitle: content?.highlightedTitle ?? DEFAULT_CONTENT.highlightedTitle,
    subtitle: content?.subtitle ?? DEFAULT_CONTENT.subtitle,
    description: content?.description ?? DEFAULT_CONTENT.description,
    workingHours: {
        ...DEFAULT_CONTENT.workingHours,
        ...(content?.workingHours || {}),
        schedule: content?.workingHours?.schedule || []
    }
})

/**
 * MicrositeContactSectionForm
 * Content editor for the landing page "Contact" section, matching
 * the real production content shape: title (with an optional
 * highlighted word/phrase), subtitle, description, and a working
 * hours block with its own title and a repeatable day/time
 * schedule. Address, phone, email, and the map embed URL are NOT
 * part of this section — they live on the store's General settings
 * (master record fields) and render from there on the live site.
 *
 * Pure presentational sub-form — receives `content` and `onChange`
 * from MicrositeLandingSection, which owns loading/saving/versioning.
 * No DB or API changes required: osm_store_config_landing.content is
 * a generic JSON column already; this form just sends the matching
 * object into the same micrositeUpdateLandingSection contract.
 */
const MicrositeContactSectionForm = ({ content, onChange }) => {
    const safe = withDefaults(content)

    const update = (field, value) => onChange?.({ ...safe, [field]: value })
    const updateWorkingHours = (field, value) =>
        onChange?.({ ...safe, workingHours: { ...safe.workingHours, [field]: value } })

    return (
        <section className={styles.fieldGroup}>
            <h2 className={styles.groupTitle}>Contact</h2>
            <p className={styles.groupDesc}>
                Your address, phone, and email come from General settings — this section covers the headline and working hours shown on the contact page.
            </p>

            <TextInput
                label="Title"
                value={safe.title}
                onChange={(v) => update('title', v)}
                placeholder="e.g. Scale Your Salon with Lokaci"
                maxLength={100}
                showCharCount
            />
            <TextInput
                label="Highlighted word/phrase (optional)"
                value={safe.highlightedTitle}
                onChange={(v) => update('highlightedTitle', v)}
                placeholder="e.g. Scale"
                helperText="A word from the title above, shown in an accent color"
            />
            <TextInput
                label="Subtitle"
                value={safe.subtitle}
                onChange={(v) => update('subtitle', v)}
                placeholder="e.g. GET IN TOUCH"
                maxLength={60}
                showCharCount
            />
            <TextInput
                label="Description"
                value={safe.description}
                onChange={(v) => update('description', v)}
                placeholder="e.g. Ready to modernize your salon operations? Reach out for a demo."
                maxLength={220}
                showCharCount
            />

            <h3 className={styles.subTitle}>Working hours</h3>
            <TextInput
                label="Block title"
                value={safe.workingHours.title}
                onChange={(v) => updateWorkingHours('title', v)}
                placeholder="Working Hours"
            />

            <label className={styles.rowsLabel}>Schedule</label>
            <MicrositeRepeatableRows
                items={safe.workingHours.schedule}
                onChange={(v) => updateWorkingHours('schedule', v)}
                emptyItem={{ ...EMPTY_SCHEDULE_ROW }}
                addLabel="Add row"
                renderSummary={(item) => item.day || 'New row'}
                renderItem={(item, idx, updateItem) => (
                    <div className={styles.row2}>
                        <TextInput
                            label="Day(s)"
                            value={item.day}
                            onChange={(v) => updateItem('day', v)}
                            placeholder="e.g. Monday - Friday"
                        />
                        <TextInput
                            label="Time"
                            value={item.time}
                            onChange={(v) => updateItem('time', v)}
                            placeholder="e.g. 09:00 - 20:00 or Closed"
                        />
                    </div>
                )}
            />
        </section>
    )
}

export default MicrositeContactSectionForm